import { useCallback, useEffect } from 'react'
import { NextRouter } from 'next/router'
import { Profile } from '@core/entities/profile'

const LAST_ORGANISATION_STORAGE_KEY = 'siteassist_last_organisation_id'

type LastOrganisationByUser = Record<string, number>

export interface LastOrganisationOperations {
  getLastOrganisationId: (userId: number) => number | null
  storeLastOrganisationId: (userId: number, organisationId: number) => void
  clearLastOrganisationId: (userId: number) => void
}

const readAll = (): LastOrganisationByUser => {
  try {
    if (typeof window === 'undefined') return {}

    const parsed = JSON.parse(
      localStorage.getItem(LAST_ORGANISATION_STORAGE_KEY) ?? '{}',
    )

    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed
      : {}
  } catch {
    return {}
  }
}

const writeAll = (value: LastOrganisationByUser) => {
  try {
    if (typeof window === 'undefined') return

    localStorage.setItem(LAST_ORGANISATION_STORAGE_KEY, JSON.stringify(value))
  } catch {
    return
  }
}

export const useLastOrganisation = (): LastOrganisationOperations => {
  const getLastOrganisationId = useCallback((userId: number): number | null => {
    const organisationId = readAll()[String(userId)]

    return Number.isInteger(organisationId) ? organisationId : null
  }, [])

  const storeLastOrganisationId = useCallback(
    (userId: number, organisationId: number): void => {
      writeAll({ ...readAll(), [String(userId)]: organisationId })
    },
    [],
  )

  const clearLastOrganisationId = useCallback((userId: number): void => {
    const all = readAll()
    delete all[String(userId)]
    writeAll(all)
  }, [])

  return {
    getLastOrganisationId,
    storeLastOrganisationId,
    clearLastOrganisationId,
  }
}

export const useRememberLastOrganisation = (
  profile: Profile | undefined,
  router: NextRouter,
): void => {
  const { storeLastOrganisationId } = useLastOrganisation()
  const organisationId = router.query?.id
    ? parseInt(`${router.query.id}`)
    : null

  // Remember the organisation only when the user is on an /organisations/
  // route and is a member of that organisation. Support impersonation, other
  // routes and organisations the user cannot access are never recorded.
  useEffect(() => {
    if (!profile || profile.impersonated || !organisationId) return
    if (!router.pathname.startsWith('/organisations/')) return
    if (!profile.isMemberOfOrganisation(organisationId)) return

    storeLastOrganisationId(profile.id, organisationId)
  }, [profile, organisationId, router.pathname, storeLastOrganisationId])
}
